import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Radio,
  ListItemButton,
  Checkbox,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArchivedChip from './ArchivedChip';
import ImagePreviewDialog from './ImagePreviewDialog';
import ProductThumbnail from './ProductThumbnail';
import RestoreOrDeleteButton from './RestoreOrDeleteButton';
import {
  isMultipleSelection,
  isView,
  isSingleSelection,
  type ListElement,
  type ListSelectionMode
} from './types';

interface CommonListProps<T> {
  items: T[];
  toListItem: (item: T) => ListElement;
  onDelete?: (itemId: number) => void;
  onRestore?: (itemId: number) => void;
  onEdit?: (item: T) => void;
  selectionMode: ListSelectionMode;
  selectedItemId?: number;
  onSelect?: (item: T) => void;
  onDoubleClick?: (item: T) => void;
  selectedItemIds?: number[];
  onToggleSelect?: (itemId: number) => void;
  emptyMessage?: string;
  renderExtra?: (item: T) => React.ReactNode;
  // Extra action buttons rendered inline, before the edit button
  renderActions?: (item: T) => React.ReactNode;
  showSelection?: boolean;
  onItemClick?: (item: T) => void;
}

const THUMBNAIL_SIZE = { xs: 80, sm: 100 };

export default function CommonList<T>({
  items,
  toListItem: renderLabel,
  onDelete,
  onRestore,
  onEdit,
  selectionMode,
  selectedItemId,
  onSelect,
  onDoubleClick,
  selectedItemIds = [],
  onToggleSelect,
  emptyMessage = 'Нічого не знайдено',
  renderExtra,
  renderActions,
  showSelection = true,
  onItemClick,
}: CommonListProps<T>) {
  const [previewImageId, setPreviewImageId] = useState<number | null>(null);

  const handleOpenPreview = (e: React.MouseEvent, imageId?: number) => {
    e.stopPropagation();
    setPreviewImageId(imageId ?? null);
  };

  const handleClosePreview = () => {
    setPreviewImageId(null);
  };

  return (
    <Box>
      {items.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {emptyMessage}
        </Typography>
      ) : (
        <List>
          {items.map((item) => {
            const label = renderLabel(item);
            return (
            <Paper
              key={label.id}
              elevation={selectedItemId === label.id ? 3 : 1}
              sx={{
                mb: 2,
                '&:hover': { elevation: 3 },
                border: selectedItemId === label.id ? '2px solid' : 'none',
                borderColor: 'primary.main',
              }}
            >
              {isSingleSelection(selectionMode) ? (
                <ListItemButton
                  onClick={() => onSelect?.(item)}
                  onDoubleClick={() => onDoubleClick?.(item)}
                >
                  <Radio
                    checked={selectedItemId === label.id}
                    value={label.id}
                    sx={{ mr: 2 }}
                  />
                  {label.imageId && (
                    <Box sx={{ mr: { xs: 1, sm: 2 } }}>
                      <ProductThumbnail
                        imageId={label.imageId}
                        size={THUMBNAIL_SIZE}
                      />
                    </Box>
                  )}
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" component="span">
                          {label.primary}
                        </Typography>
                        {label.secondary && (
                          <Typography variant="body2" color="text.secondary" sx={{ bgcolor: 'action.hover', px: 1, borderRadius: 1 }}>
                            {label.secondary}
                          </Typography>
                        )}
                        {label.tertiary && (
                          <Typography variant="caption" color="primary" sx={{ bgcolor: 'primary.50', px: 1, borderRadius: 1, fontWeight: 600, textTransform: 'uppercase' }}>
                            {label.tertiary}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              ) : (
                <ListItem
                  onClick={isView(selectionMode) ? () => onItemClick?.(item) : undefined}
                  sx={{
                    px: { xs: 1, sm: 2 },
                    flexWrap: 'wrap',
                    rowGap: 1,
                    cursor: isView(selectionMode) && onItemClick ? 'pointer' : 'default',
                  }}
                >
                  {showSelection && (isView(selectionMode) || isMultipleSelection(selectionMode)) && (
                    <Checkbox
                      size="small"
                      checked={selectedItemIds.includes(label.id)}
                      onChange={() => onToggleSelect?.(label.id)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        mr: { xs: 0.5, sm: 1 },
                        p: { xs: 0.5, sm: 1 },
                      }}
                    />
                  )}
                  {label.imageId && (
                    <Box sx={{ mr: { xs: 1, sm: 2 } }}>
                      <ProductThumbnail
                        imageId={label.imageId}
                        size={THUMBNAIL_SIZE}
                        onClick={(e) => handleOpenPreview(e, label.imageId)}
                      />
                    </Box>
                  )}
                  <ListItemText
                    sx={{
                      flex: { xs: label.imageId ? '1 1 80px' : '1 1 160px', sm: '1 1 160px' },
                      minWidth: 0,
                      my: 0,
                    }}
                    primary={
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="h6"
                          component="div"
                          color={label.archived ? 'text.secondary' : 'text.primary'}
                        >
                          {label.primary}
                        </Typography>
                        {label.secondary && (
                          <Typography variant="body2" color="text.secondary" sx={{ bgcolor: 'action.hover', px: 1, borderRadius: 1 }}>
                            {label.secondary}
                          </Typography>
                        )}
                        {label.tertiary && (
                          <Typography variant="caption" color="primary" sx={{ bgcolor: 'primary.50', px: 1, borderRadius: 1, fontWeight: 600, textTransform: 'uppercase' }}>
                            {label.tertiary}
                          </Typography>
                        )}
                        {label.archived && <ArchivedChip />}
                      </Box>
                    }
                  />
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
                  >
                    {renderActions && !label.archived && renderActions(item)}
                    {onEdit && !label.archived && (
                      <IconButton
                        aria-label="edit"
                        onClick={() => onEdit(item)}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    <RestoreOrDeleteButton
                      archived={!!label.archived}
                      onDelete={onDelete ? () => onDelete(label.id) : undefined}
                      onRestore={onRestore ? () => onRestore(label.id) : undefined}
                    />
                  </Box>
                </ListItem>
              )}
              {renderExtra?.(item)}
            </Paper>
            );
          })}
        </List>
      )}

      <ImagePreviewDialog
        imageId={previewImageId}
        onClose={handleClosePreview}
      />
    </Box>
  );
}
